import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Dialog } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { CardContent } from '@/shared/components/card-content/card-content';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import { PerfisService } from '@/core/api/sicom-api/sistema/perfis/perfis.service';
import { PerfilRequest, SincronizacaoPreview } from '@/core/api/sicom-api/sistema/perfis/perfis.model';
import { RolesAdminService } from '@/core/api/sicom-api/sistema/roles/roles-admin.service';
import { UsuarioService } from '@/core/api/sicom-api/sistema/usuario/usuario.service';
import { UsuarioResumoResponse } from '@/core/api/sicom-api/sistema/usuario/usuario.model';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { AuthService } from '@/core/auth/auth.service';
import { PermissionService } from '@/core/permissions/permission.service';
import { UF_CFM } from '@/core/permissions/permission.model';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { agruparRoles, FuncionalidadeGrupo, ModuloGrupo } from '../roles-agrupamento.util';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
@Component({
    selector: 'app-perfil-detalhe',
    standalone: true,
    imports: [DialogoPadraoDirective, FormsModule, Dialog, ButtonModule, InputTextModule, ToggleSwitchModule, TooltipModule, IconFieldModule, InputIconModule, ConfirmDialogModule, CardContent, CanDirective, SelectEstado],
    providers: [ConfirmationService],
    templateUrl: './perfil-detalhe.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/**
 * Editor de perfil (criar/editar) com seletor de roles por módulo/funcionalidade/ação.
 * "Criar a partir de um usuário" apenas PRÉ-MARCA as roles do usuário escolhido no seletor —
 * o perfil nasce independente dele; por isso o fluxo usa sempre `criar`/`editar`, nunca `criarDeUsuario`.
 *
 * Sincronização: ao salvar em modo edição com roles alteradas, uma prévia (read-only,
 * `previewSincronizacao`) é consultada para saber quantos usuários têm o perfil e qual o delta.
 * Só se houver usuários afetados é que um diálogo oferece escolher entre sincronizar ou não — o
 * delta em si é sempre calculado no servidor, o front só exibe.
 */
export class PerfilDetalhe implements OnInit {
    private readonly perfisService = inject(PerfisService);
    private readonly rolesService = inject(RolesAdminService);
    private readonly usuarioService = inject(UsuarioService);
    private readonly notificacoes = inject(NotificacoesService);
    private readonly confirm = inject(ConfirmationService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly auth = inject(AuthService);
    private readonly permissionService = inject(PermissionService);

    /** `null` = novo perfil; número = editando esse id. */
    idPerfil = input<number | null>(null);
    /** Salvo com sucesso — o shell fecha o diálogo e recarrega o catálogo. */
    salvou = output<void>();
    /** Fechado sem salvar (cancelar / esc / clique fora) — o shell só fecha o diálogo. */
    fechado = output<void>();

    protected readonly nmPerfil = signal('');
    protected readonly dsPerfil = signal('');
    protected readonly uf = signal('');

    protected readonly tentouSalvar = signal(false);
    protected readonly salvando = signal(false);
    protected readonly carregandoCatalogo = signal(false);
    protected readonly carregandoPerfil = signal(false);
    protected readonly carregando = computed(() => this.carregandoCatalogo() || this.carregandoPerfil());

    protected readonly modoEdicao = computed(() => this.idPerfil() != null);

    /**
     * A UF não é escolhível quando: (a) o operador é regional — o servidor recusaria com 403
     * qualquer outra, e a tela não anuncia escolha que o backend nega; (b) é edição — a UF é
     * imutável depois da criação (o backend recusa a troca com 409).
     */
    protected readonly ufBloqueada = computed(() => this.modoEdicao() || !this.permissionService.isCfm);

    /**
     * Razão de o campo UF estar travado — um texto para **cada** estado bloqueado, vazio quando o
     * campo está livre. Vive no `<label>` (ver `perfil-detalhe.html`): no seletor desabilitado
     * o PrimeNG aplica `pointer-events: none` e o tooltip nunca dispararia.
     */
    protected readonly ufTooltip = computed(() => {
        if (this.modoEdicao()) return 'A UF do perfil não pode ser alterada';
        if (!this.permissionService.isCfm) return `Seu perfil só pode criar perfis da UF ${this.uf()}`;
        return '';
    });

    protected readonly nomeInvalido = computed(() => !this.nmPerfil().trim());
    protected readonly mostrarErroNome = computed(() => this.tentouSalvar() && this.nomeInvalido());

    protected readonly rolePermissaoAdministrar = PERMISSOES.configuracoes.usuarios.permissoes.administrar;

    protected readonly grupos = signal<ModuloGrupo[]>([]);
    protected readonly selecionadas = signal<Set<string>>(new Set());
    /** Roles como carregadas do servidor (modo edição) — referência para detectar se a seleção mudou. */
    private rolesOriginais = new Set<string>();

    // ── Diálogo de sincronização: só abre se a prévia indicar usuários afetados ──
    protected readonly dialogSyncAberto = signal(false);
    protected readonly syncPreview = signal<SincronizacaoPreview | null>(null);
    private pendenteSync: { id: number; req: PerfilRequest } | null = null;

    // ── Kanban: módulo/funcionalidade selecionados ──
    protected readonly moduloSel = signal<string | null>(null);
    protected readonly funcSel = signal<string | null>(null);
    protected readonly moduloAtual = computed(() => this.grupos().find((m) => m.modulo === this.moduloSel()) ?? null);
    protected readonly funcAtual = computed(() => this.moduloAtual()?.funcionalidades.find((f) => f.funcionalidade === this.funcSel()) ?? null);

    // ── Snapshot de usuário ("criar a partir de um usuário") ──
    protected readonly snapshotAberto = signal(false);
    protected readonly usuarios = signal<UsuarioResumoResponse[]>([]);
    protected readonly filtroUsuarioSnapshot = signal('');
    protected readonly usuarioSnapshotSel = signal<UsuarioResumoResponse | null>(null);
    protected readonly usuariosFiltrados = computed(() => {
        const t = this.filtroUsuarioSnapshot().toLowerCase().trim();
        return this.usuarios().filter((u) => !t || (u.nmSolicitante ?? '').toLowerCase().includes(t) || (u.userId ?? '').toLowerCase().includes(t));
    });

    ngOnInit(): void {
        this.carregarCatalogo();
        const id = this.idPerfil();
        // Regional já nasce com a UF dele; CFM começa em nacional e pode trocar.
        this.uf.set(this.permissionService.isCfm ? UF_CFM : (this.auth.ufDoUsuario() ?? ''));
        if (id != null) this.carregarPerfil(id);
    }

    private carregarCatalogo(): void {
        this.carregandoCatalogo.set(true);
        this.rolesService
            .listarRoles()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (roles) => {
                    const grupos = agruparRoles(roles);
                    this.grupos.set(grupos);
                    this.selecionarModulo(grupos[0] ?? null);
                    this.carregandoCatalogo.set(false);
                },
                error: () => {
                    this.carregandoCatalogo.set(false);
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao carregar roles' });
                }
            });
    }

    private carregarPerfil(id: number): void {
        this.carregandoPerfil.set(true);
        this.perfisService
            .buscar(id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (perfil) => {
                    this.nmPerfil.set(perfil.nmPerfil);
                    this.dsPerfil.set(perfil.dsPerfil ?? '');
                    this.uf.set(perfil.uf);
                    this.selecionadas.set(new Set(perfil.roles));
                    this.rolesOriginais = new Set(perfil.roles);
                    this.carregandoPerfil.set(false);
                },
                error: () => {
                    this.carregandoPerfil.set(false);
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao carregar perfil' });
                }
            });
    }

    protected selecionarModulo(mod: ModuloGrupo | null): void {
        this.moduloSel.set(mod?.modulo ?? null);
        this.funcSel.set(mod?.funcionalidades[0]?.funcionalidade ?? null);
    }
    protected selecionarFunc(f: FuncionalidadeGrupo): void {
        this.funcSel.set(f.funcionalidade);
    }

    protected marcada(role: string): boolean {
        return this.selecionadas().has(role);
    }
    protected toggle(role: string): void {
        const s = new Set(this.selecionadas());
        if (s.has(role)) s.delete(role);
        else s.add(role);
        this.selecionadas.set(s);
        // Seleção mexida à mão: o aviso "pré-marcado a partir de X" deixa de refletir a realidade.
        this.usuarioSnapshotSel.set(null);
    }
    protected crudTodas(f: FuncionalidadeGrupo): boolean {
        return f.crud.length > 0 && f.crud.every((a) => this.marcada(a.role));
    }
    protected toggleCrud(f: FuncionalidadeGrupo): void {
        const ligar = !this.crudTodas(f);
        const s = new Set(this.selecionadas());
        for (const a of f.crud) {
            if (ligar) s.add(a.role);
            else s.delete(a.role);
        }
        this.selecionadas.set(s);
        this.usuarioSnapshotSel.set(null);
    }

    protected abrirSnapshot(): void {
        this.snapshotAberto.set(true);
        if (!this.usuarios().length) this.carregarUsuarios();
    }
    protected fecharSnapshot(): void {
        this.snapshotAberto.set(false);
    }
    private carregarUsuarios(): void {
        this.usuarioService
            .listar({ size: 200 })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (p) => this.usuarios.set(p.content),
                error: () => this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao carregar usuários' })
            });
    }
    /**
     * Pré-marca no seletor as roles atuais do usuário escolhido — ponto de partida editável, não vínculo.
     * Se já houver roles marcadas, confirma antes: a troca é uma substituição total, não uma união.
     */
    protected selecionarUsuarioSnapshot(u: UsuarioResumoResponse): void {
        if (this.selecionadas().size === 0) {
            this.aplicarSnapshot(u);
            return;
        }
        this.confirm.confirm({
            header: 'Substituir seleção de roles',
            message: `As ${this.selecionadas().size} role(s) marcada(s) serão substituídas pelas roles de "${u.nmSolicitante || u.userId}". Deseja continuar?`,
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Substituir',
            rejectLabel: 'Cancelar',
            accept: () => this.aplicarSnapshot(u)
        });
    }

    private aplicarSnapshot(u: UsuarioResumoResponse): void {
        this.rolesService
            .rolesDoUsuario(u.userId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (roles) => {
                    this.selecionadas.set(new Set(roles.map((r) => r.name)));
                    this.usuarioSnapshotSel.set(u);
                    this.snapshotAberto.set(false);
                    this.notificacoes.notificar({ severity: 'success', summary: `Roles de ${u.nmSolicitante || u.userId} pré-marcadas.` });
                },
                error: () => this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao carregar roles do usuário' })
            });
    }

    protected salvar(): void {
        this.tentouSalvar.set(true);
        if (this.nomeInvalido()) return;
        const req: PerfilRequest = {
            nmPerfil: this.nmPerfil().trim(),
            dsPerfil: this.dsPerfil().trim() || undefined,
            uf: this.uf(),
            roles: [...this.selecionadas()]
        };
        const id = this.idPerfil();

        if (id == null) {
            this.salvando.set(true);
            this.perfisService
                .criar(req)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: () => {
                        this.salvando.set(false);
                        this.notificacoes.notificar({ severity: 'success', summary: 'Perfil criado.' });
                        this.salvou.emit();
                    },
                    error: () => {
                        this.salvando.set(false);
                        this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao salvar perfil' });
                    }
                });
            return;
        }

        if (!this.rolesMudaram(req.roles)) {
            this.executarSalvar(id, req, false);
            return;
        }

        // Roles mudaram: consulta a prévia (read-only) antes de decidir se abre o diálogo.
        this.salvando.set(true);
        this.perfisService
            .previewSincronizacao(id, req.roles)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (preview) => {
                    this.salvando.set(false);
                    if (preview.qtdUsuarios === 0) {
                        // Ninguém tem o perfil ainda: nada para sincronizar, sem fricção.
                        this.executarSalvar(id, req, false);
                        return;
                    }
                    this.pendenteSync = { id, req };
                    this.syncPreview.set(preview);
                    this.dialogSyncAberto.set(true);
                },
                error: () => {
                    this.salvando.set(false);
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao verificar sincronização' });
                }
            });
    }

    private rolesMudaram(rolesNovas: string[]): boolean {
        if (rolesNovas.length !== this.rolesOriginais.size) return true;
        return rolesNovas.some((r) => !this.rolesOriginais.has(r));
    }

    /** "Salvar e sincronizar": propaga o delta (calculado no servidor) para os usuários do perfil. */
    protected confirmarSalvarComSync(): void {
        const pendente = this.pendenteSync;
        const qtdUsuarios = this.syncPreview()?.qtdUsuarios;
        this.fecharDialogSync();
        if (pendente) this.executarSalvar(pendente.id, pendente.req, true, qtdUsuarios);
    }

    /** "Salvar sem sincronizar": grava o perfil, mas os usuários que já o têm ficam como estão. */
    protected confirmarSalvarSemSync(): void {
        const pendente = this.pendenteSync;
        this.fecharDialogSync();
        if (pendente) this.executarSalvar(pendente.id, pendente.req, false);
    }

    /** "Cancelar": fecha o diálogo sem salvar nada. */
    protected cancelarSync(): void {
        this.fecharDialogSync();
    }

    private fecharDialogSync(): void {
        this.dialogSyncAberto.set(false);
        this.syncPreview.set(null);
        this.pendenteSync = null;
    }

    private executarSalvar(id: number, req: PerfilRequest, sincronizar: boolean, qtdUsuariosSincronizados?: number): void {
        this.salvando.set(true);
        this.perfisService
            .editar(id, req, sincronizar)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.salvando.set(false);
                    const summary = sincronizar && qtdUsuariosSincronizados ? `Perfil salvo e ${qtdUsuariosSincronizados} usuário(s) sincronizado(s).` : 'Perfil atualizado.';
                    this.notificacoes.notificar({ severity: 'success', summary });
                    this.salvou.emit();
                },
                error: () => {
                    this.salvando.set(false);
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao salvar perfil' });
                }
            });
    }

    protected fechar(): void {
        this.fechado.emit();
    }
}
